import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import ReportLayout from './components/ReportLayout';
import { Calendar, Building2, ChevronDown } from 'lucide-react';
import useDailySales from '@/hooks/useDailySales';

export default function DailySalesReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.SALES.SCREEN)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
          <p className="text-sm text-slate-500">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }
  const today = new Date().toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [sortColumn, setSortColumn] = useState('sale_date');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [ncFilter, setNcFilter] = useState('');

const initialFilters = useMemo(() => ({
  startDate: today,
  endDate: today,
  offset: 0,
  limit: 25,
}), []);

  const {
    data,
    summary,
    branchSummary,
    totalItems,
    loading,
    setFilters,
    refreshSales
  } = useDailySales(initialFilters);

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

const computedSummary = useMemo(() => ({
  totalOrders:
    summary?.totalOrders ??
    summary?.total_orders ??
    0,

  totalGrossSales:
    summary?.grossSales ??
    summary?.gross_sales ??
    0,

  totalNetSales:
    summary?.netSales ??
    summary?.net_sales ??
    0,

  totalDiscounts:
    summary?.discountAmount ??
    summary?.discount_amount ??
    0,

  totalNcOrders: summary?.ncOrders ?? summary?.nc_orders ?? 0,
  totalNcAmount: summary?.ncAmount ?? summary?.nc_amount ?? 0,
}), [summary]);

const handleDateChange = (date) => {
  setSelectedDate(date);
  setCurrentPage(1);
  setFilters(prev => ({
    ...prev,
    period: null,
    startDate: date,
    endDate: date,
    offset: 0,
    limit: pageSize,
  }));
};

//export file as csv
const exportToCsv = () => {
  if (!data?.length) return;

  const headers = columns.map(c => `"${c.label}"`).join(',');
  const rows = data.map(r =>
    columns.map(c => `"${r[c.key] ?? ''}"`).join(',')
  );

  const blob = new Blob([headers, '\n', rows.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'daily-sales-report.csv';
  link.click();
};


  const summaryCards = [
    { title: 'Total Orders', value: computedSummary.totalOrders.toLocaleString(), icon: Calendar },
    { title: 'Gross Sales', value: `₹${computedSummary.totalGrossSales.toLocaleString()}`, icon: Calendar },
    { title: 'Net Sales', value: `₹${computedSummary.totalNetSales.toLocaleString()}`, icon: Calendar },
    { title: 'Discounts Given', value: `₹${computedSummary.totalDiscounts.toLocaleString()}`, icon: Calendar },
    { title: 'NC Amount', value: `₹${(computedSummary.totalNcAmount ?? 0).toLocaleString()}`, icon: Calendar },
  ];


  const columns = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: (v) =>
        v
          ? new Date(v).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })
          : '—'
    },
    { key: 'orders', label: 'Orders', sortable: true, align: 'right' },
    { key: 'grossSales', label: 'Gross Sales', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'discounts', label: 'Discounts', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'ncOrders', label: 'NC Orders', sortable: true, align: 'right' },
    { key: 'ncAmount', label: 'NC Amount', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'netSales', label: 'Net Sales', sortable: true, align: 'right',
      render: v => <span className="font-semibold">₹{Number(v ?? 0).toLocaleString()}</span> },
    { key: 'tax', label: 'Tax', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'cash', label: 'Cash', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'upi', label: 'UPI', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'card', label: 'Card', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
    { key: 'avgOrderValue', label: 'Avg Order', sortable: true, align: 'right',
      render: v => `₹${Number(v ?? 0).toLocaleString()}` },
  ];

const filters = (
  <div className="flex items-center gap-3">
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">Date</label>
      <input
        type="date"
        value={selectedDate}
        max={today}
        onChange={(e) => handleDateChange(e.target.value)}
        className="h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
      />
    </div>

    {branchOptions.length > 0 && (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600">Branch</label>
        <div className="relative">
          <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedBranchId(val);
              setCurrentPage(1);
              setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
            }}
            className="h-9 pl-9 pr-8 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
          >
            <option value="">All Branches</option>
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>
    )}

    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">Bill Type</label>
      <select
        value={ncFilter}
        onChange={(e) => {
          const val = e.target.value;
          setNcFilter(val);
          setCurrentPage(1);
          setFilters(prev => ({ ...prev, ncFilter: val || undefined, offset: 0 }));
        }}
        className="h-9 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
      >
        <option value="">All Bills</option>
        <option value="exclude_nc">Regular Only</option>
        <option value="nc_only">NC Only</option>
      </select>
    </div>
  </div>
);

    //SORTING
const sortedData = useMemo(() => {
  if (!data || !sortColumn) return data;

  const sorted = [...data];

  sorted.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === 'date' || sortColumn === 'sale_date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    }

    return sortDirection === 'asc'
      ? String(aVal ?? '').localeCompare(String(bVal ?? ''))
      : String(bVal ?? '').localeCompare(String(aVal ?? ''));
  });

  return sorted;
}, [data, sortColumn, sortDirection]);

// CLIENT-SIDE FILTERING - Search across all columns
const filteredData = useMemo(() => {
  if (!searchQuery.trim() || !sortedData) return sortedData;

  const query = searchQuery.toLowerCase().trim();

  return sortedData.filter(row => {
    // Search in date (formatted)
    if (row.date) {
      const dateStr = new Date(row.date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).toLowerCase();
      if (dateStr.includes(query)) return true;
    }

    // Search in numeric fields (orders, sales, discounts, tax, etc.)
    const numericFields = ['orders', 'grossSales', 'discounts', 'netSales', 'tax', 'cash', 'upi', 'card', 'avgOrderValue'];
    for (const field of numericFields) {
      if (row[field] !== undefined && row[field] !== null) {
        const value = Number(row[field]);
        // Match exact number or formatted number with commas
        if (String(value).includes(query) || value.toLocaleString().toLowerCase().includes(query)) {
          return true;
        }
      }
    }

    return false;
  });
}, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Daily Sales Summary"
      description="Day-wise revenue breakdown with payment modes"
      icon={Calendar}
      exportPermission={P.REPORTS.SALES.DAILY_EXPORT}
      color="teal"
      category="Sales"
      categoryId="sales"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      pagination={{
        currentPage,
        totalPages: Math.ceil((totalItems || 0) / pageSize),
        totalItems: totalItems || 0,
        pageSize,
        onPageChange: (page) => {
          setCurrentPage(page);
          setFilters(p => ({ ...p, offset: (page - 1) * pageSize }));
        },
        onPageSizeChange: (size) => {
          setPageSize(size);
          setFilters(p => ({ ...p, limit: size, offset: 0 }));
        }
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
        let nextDirection;

        if (sortColumn === column) {
          // toggle
          nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          // default sort direction
          nextDirection =
            column === 'date' || column === 'sale_date'
              ? 'asc'
              : 'desc';
        }

        setSortColumn(column);
        setSortDirection(nextDirection);
      }}
      onExport={(format) => {
        if (format === 'csv') exportToCsv();
      }}
      onRefresh={() => handleDateChange(selectedDate)}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by date, amount, orders..."
    />
  );
}
